import { nanoid } from 'nanoid';
import type { Comparison } from '@makemydeal/dr-dash-bff-types';

/**
 * Predicate used to filter a list of Comparisons to get the published & active one
 * @param comparison
 * @returns
 */
export const byPublishedActiveStatus = (comparison: Comparison) => comparison.isPublished && comparison.status === 'active';

/**
 * Predicate used to filter a list of Comparisons to get the unpublished & active one
 * @param comparison
 * @returns
 */
export const byUnpublishedActiveStatus = (comparison: Comparison) => !comparison.isPublished && comparison.status === 'active';

/**
 * Rebuilds the order array based on the comparison object and successfully retrieved alternate scenario ids
 * @param comparison
 * @param retrievedNonPrimaryScenarioIds
 * @param primaryScopeId
 * @returns
 */
export const getOrderFromRetrievedScenarios = (
    comparison: Comparison,
    retrievedNonPrimaryScenarioIds: any[],
    primaryScopeId: string
): string[] => {
    const order: string[] = [];

    // Rebuild the order array correctly with retrieved versionIds instead of generated ids
    if (comparison.scenarios) {
        for (const scenario of comparison.scenarios) {
            // don't try to hydrate a scope from a scenarioId we did not successfully retrieve
            if (!scenario.isPrimary && retrievedNonPrimaryScenarioIds.includes(scenario.id)) {
                order.push(scenario.id);
            } else {
                order.push(primaryScopeId);
            }
        }
    }

    return order;
};

/**
 * Builds the order array with generated non-primary scenarios ids for a template comparison
 * @param comparison
 * @param retrievedNonPrimaryScenarioIds
 * @param primaryScopeId
 * @returns
 */
export const getOrderForTemplateScenarios = (comparison: Comparison, primaryScopeId: string): string[] => {
    const nonPrimaryScenarios = comparison.scenarios.filter(({ isPrimary }) => !isPrimary);
    const generatedIds = nonPrimaryScenarios.map(() => nanoid());

    return [primaryScopeId, ...generatedIds];
};

/**
 * Reserves a scope slot per template scenario, keyed by position, for a multi-scenario template.
 * A template comparison reuses the primary deal-version id for every scenario, so scopes can't be
 * keyed by id — each slot is hydrated later by the `scenarioIndex` on its scenario frame.
 *
 * The primary scope keeps its id at its saved index (NOT necessarily 0); other slots get `nanoid()` ids.
 *
 * @param scenarioCount total number of scenarios in the template
 * @param primaryScenarioIndex the primary scenario's saved index (from the bootstrap `template` object)
 * @param primaryScopeId the id of the primary scope (`__primaryScope`)
 */
export const getOrderForTemplateScenariosByIndex = (
    scenarioCount: number,
    primaryScenarioIndex: number,
    primaryScopeId: string
): string[] => {
    return Array.from({ length: scenarioCount }, (_, index) => (index === primaryScenarioIndex ? primaryScopeId : nanoid()));
};

/**
 * Retrieves the latest draft or published comparison from a list
 *
 * @param comparisonList The full list of Comparison objects retrieved from baggage
 * @param latestDxgVersion The latest published DXG version
 * @returns Either the active draft comparison, or the active published comparison if it's the same as the latest version
 */
export const getActiveComparison = (comparisonList: Comparison[], latestDxgVersion: string) => {
    const activePublishedComparison = comparisonList.find(byPublishedActiveStatus);
    const activeUnpublishedComparison = comparisonList.find(byUnpublishedActiveStatus);
    // Either pick the (unpublished) draft comparison, or the
    // published comparison if it's the same as the latest version
    const activeComparison =
        activeUnpublishedComparison ||
        (activePublishedComparison?.dealXgVersionId === latestDxgVersion && activePublishedComparison);
    return activeComparison;
};

/**
 * Merges the new active comparison with the current one
 * @param newActiveComparison The new active comparison to merge
 * @param currentActiveComparison The current active comparison
 * @returns The merged active comparison
 */
export const mergeActiveComparison = (newActiveComparison: Comparison, currentActiveComparison?: Comparison): Comparison => {
    if (!currentActiveComparison) {
        return newActiveComparison;
    }
    return {
        ...currentActiveComparison,
        scenarios: currentActiveComparison.scenarios.map((scenario) => {
            return newActiveComparison.scenarios.find((newScenario) => newScenario.id === scenario.id) || scenario;
        })
    };
};

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

/**
 * Validates an inbound async scenario frame before per-scenario hydration.
 *
 * Returns the scenario's scope id (`offer.dealExchangeVersionId`) when the frame can be routed to a
 * scope the factory created at BOOTSTRAP_SUCCESS; otherwise records a diagnostic `MV:WebSocket`
 * PageAction and returns `undefined` so the caller drops the frame silently (FR-8: silent to the
 * user, observable in New Relic — never a log line).
 *
 * Two reject cases:
 *  - `scenarioMissingId`    — the frame carries no `offer.dealExchangeVersionId`, so it can't be routed.
 *  - `scenarioScopeMissing` — the id isn't one the factory reserved a scope for. This is the symptom of
 *    a server/client active-comparison mismatch (the backend fanned out scenario ids the client never
 *    created a scope for). Hydrating a non-existent scope throws in scopedStoreManager, so we drop it —
 *    but surface the divergence (with the known scope ids) so it doesn't vanish without a trace.
 *
 * @param scenario the decoded scenario payload from the WebSocket frame
 * @param knownScopeIds the current scope order (from `scopedStateSelectors.getScopesOrder`)
 * @returns the scope id to hydrate, or `undefined` when the frame should be dropped
 */
export const validateScenario = (scenario: any, knownScopeIds: string[]): string | undefined => {
    const scenarioId = scenario?.offer?.dealExchangeVersionId;
    if (!scenarioId) {
        logNewRelicPageAction('MV:WebSocket', { type: 'scenarioMissingId' });
        return undefined;
    }

    if (!knownScopeIds.includes(scenarioId)) {
        logNewRelicPageAction('MV:WebSocket', {
            type: 'scenarioScopeMissing',
            scenarioId,
            knownScopeIds: knownScopeIds.join(','),
            knownScopeCount: knownScopeIds.length
        });
        return undefined;
    }

    return scenarioId;
};

/**
 * True when a scenario payload carries a template `scenarioIndex`, i.e. it belongs to a multi-scenario
 * template application and must be routed to a scope by position rather than by deal-version id.
 */
export const isTemplateScenarioPayload = (scenario: any): boolean => Number.isFinite(scenario?.template?.scenarioIndex);

/**
 * Routes a template scenario frame to the reserved scope at `order[scenarioIndex]`.
 *
 * Logs `MV:WebSocket` and returns `undefined` when unroutable: index missing/out of range, or it
 * maps to the primary scope, which is already hydrated from the initTemplate/bootstrap response.
 *
 * @param scenario the decoded scenario payload (carries `template.scenarioIndex`)
 * @param order the current scope order (from `scopedStateSelectors.getScopesOrder`)
 * @param primaryScopeId the primary scope id (from `scopedStateSelectors.getPrimaryScopeId`)
 * @returns the scope id to hydrate, or `undefined` when the frame should be dropped
 */
export const resolveTemplateScenarioScopeId = (scenario: any, order: string[], primaryScopeId: string): string | undefined => {
    const scenarioIndex = scenario?.template?.scenarioIndex;
    const scopeId = order[scenarioIndex];

    if (!scopeId || scopeId === primaryScopeId) {
        logNewRelicPageAction('MV:WebSocket', {
            type: 'templateScenarioUnroutable',
            scenarioIndex,
            knownScopeCount: order.length
        });
        return undefined;
    }

    return scopeId;
};
